import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * useColumnResize — hook for column drag-to-resize with localStorage persistence
 *
 * @param {Array<{key: string, width: number}>} columns — visible column definitions
 * @param {string} storageKey — localStorage key prefix
 * @returns {{ columnWidths, getColumnWidth, resizingColumn, totalWidth, handleResizeStart, handleDoubleClick }}
 */
export function useColumnResize(columns, storageKey) {
  const fullKey = `pcc-datatable-${storageKey}-widths`;

  // Build default widths map from column definitions
  const defaultWidths = useMemo(() => {
    const map = {};
    columns.forEach(col => { map[col.key] = col.width || 120; });
    return map;
  }, [columns]);

  // Persisted widths from localStorage
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(fullKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Resize drag state
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem(fullKey, JSON.stringify(columnWidths));
  }, [columnWidths, fullKey]);

  const getColumnWidth = useCallback((key) => {
    return columnWidths[key] || defaultWidths[key] || 120;
  }, [columnWidths, defaultWidths]);

  // Total table width = sum of all visible columns
  const totalWidth = useMemo(() => {
    return columns.reduce((sum, col) => sum + getColumnWidth(col.key), 0);
  }, [columns, getColumnWidth]);

  const handleResizeStart = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(getColumnWidth(columnKey));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [getColumnWidth]);

  const handleResizeMove = useCallback((e) => {
    if (!resizingColumn) return;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);
    setColumnWidths(prev => ({ ...prev, [resizingColumn]: newWidth }));
  }, [resizingColumn, startX, startWidth]);

  const handleResizeEnd = useCallback(() => {
    setResizingColumn(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Global mouse listeners during drag
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, handleResizeMove, handleResizeEnd]);

  // Double-click resets column to default width
  const handleDoubleClick = useCallback((e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setColumnWidths(prev => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  return {
    columnWidths,
    getColumnWidth,
    resizingColumn,
    totalWidth,
    handleResizeStart,
    handleDoubleClick,
  };
}
